import React from "react";
import {
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CButton,
  CRow,
  CCol,
} from "@coreui/react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Field } from "../__sections";

import DeleteIcon from "../../../components/icons/DeleteIcon";
import PlusIcon from "../../../components/icons/PlusIcon";
import { colors } from "../../../config/theme";
import {
  AGE_GROUPS,
  FLAT_PRICE_TYPES,
  PRICING_TYPES,
  WEEKDAYS,
} from "../../../helpers/types";
import TimeSelectInput from "../../../components/inputs/TimeSelectInput";

/** Reads a nested error message by dot-path without throwing on missing segments. */
function getIn(obj: any, path: string): any {
  return path
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

/** Consistent section header used across this editor's sub-sections. */
function SubSectionHeader({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <span
        className="fw-semibold"
        style={{ fontSize: "0.85rem", color: "#555", letterSpacing: "0.02em" }}
      >
        {title.toUpperCase()}
      </span>
      {action}
    </div>
  );
}

interface ScheduleEditorProps {
  /** e.g. "schedules.0" or "options.2.schedule" */
  namePrefix: string;
}

export default function ScheduleEditor({ namePrefix }: ScheduleEditorProps) {
  const { register, control, watch, setValue, formState } = useFormContext();
  const errors = formState.errors as any;

  const pricingType = watch(`${namePrefix}.pricingType`);

  const {
    fields: ageGroupFields,
    append: appendAgeGroup,
    remove: removeAgeGroup,
  } = useFieldArray({ control, name: `${namePrefix}.ageGroups` as const });

  const {
    fields: exceptionFields,
    append: appendException,
    remove: removeException,
  } = useFieldArray({ control, name: `${namePrefix}.exceptions` as const });

  const addSlot = (day: string) => {
    const current: string[] = watch(`${namePrefix}.weeklySlots.${day}`) || [];
    setValue(`${namePrefix}.weeklySlots.${day}`, [...current, "09:00"], {
      shouldValidate: true,
    });
  };

  const removeSlot = (day: string, idx: number) => {
    const current: string[] = watch(`${namePrefix}.weeklySlots.${day}`) || [];
    setValue(
      `${namePrefix}.weeklySlots.${day}`,
      current.filter((_, i) => i !== idx),
      { shouldValidate: true },
    );
  };

  const updateSlot = (day: string, idx: number, value: string) => {
    const current: string[] = watch(`${namePrefix}.weeklySlots.${day}`) || [];
    const next = [...current];
    next[idx] = value;
    setValue(`${namePrefix}.weeklySlots.${day}`, next, {
      shouldValidate: true,
    });
  };

  const cardStyle: React.CSSProperties = {
    border: "1px solid #ececec",
    borderRadius: "12px",
    padding: "1rem 1.1rem",
  };

  return (
    <div className="d-flex flex-column gap-3">
      {/* ── Identity & activation ─────────────────────────────── */}
      <div style={cardStyle}>
        <CRow className="align-items-end">
          <CCol md={5}>
            <Field
              label="Nom du calendrier"
              required
              error={getIn(errors, `${namePrefix}.name`)?.message}
            >
              <CFormInput
                {...register(`${namePrefix}.name`)}
                placeholder="ex: Haute saison"
              />
            </Field>
          </CCol>
          <CCol md={3}>
            <Field
              label="Date de début"
              required
              error={getIn(errors, `${namePrefix}.startDate`)?.message}
            >
              <CFormInput
                type="date"
                {...register(`${namePrefix}.startDate`)}
              />
            </Field>
          </CCol>
          <CCol md={3}>
            <Field label="Date de fin" hint="optionnel">
              <CFormInput type="date" {...register(`${namePrefix}.endDate`)} />
            </Field>
          </CCol>
          <CCol md={1}>
            <div className="d-flex flex-column align-items-center">
              <span
                className="small text-muted mb-2"
                style={{ fontSize: "0.7rem" }}
              >
                Actif
              </span>
              <CFormSwitch
                id={`${namePrefix}-isActive`}
                {...register(`${namePrefix}.isActive`)}
              />
            </div>
          </CCol>
        </CRow>
      </div>

      {/* ── Weekly slots ───────────────────────────────────────── */}
      <div style={cardStyle}>
        <SubSectionHeader title="Créneaux hebdomadaires" />
        <div className="d-flex flex-wrap gap-2">
          {WEEKDAYS.map(({ key, label }) => {
            const slots: string[] =
              watch(`${namePrefix}.weeklySlots.${key}`) || [];
            const hasSlots = slots.length > 0;
            return (
              <div
                key={key}
                style={{
                  minWidth: 140,
                  flex: "1 1 140px",
                  border: hasSlots
                    ? `1px solid ${colors.yellow}33`
                    : "1px solid #ececec",
                  backgroundColor: hasSlots ? "#fffdf5" : "#fafafa",
                  borderRadius: 10,
                  padding: "0.65rem",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold small">{label}</span>
                  <button
                    type="button"
                    onClick={() => addSlot(key)}
                    className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "6px",
                      backgroundColor: `${colors.primary}14`,
                    }}
                    title="Ajouter un créneau"
                  >
                    <PlusIcon width="12" fill={colors.primary} />
                  </button>
                </div>
                {slots.length === 0 && (
                  <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                    Fermé
                  </div>
                )}
                <div className="d-flex flex-column gap-1">
                  {slots.map((slot, idx) => (
                    <div key={idx} className="d-flex align-items-center gap-1">
                      <TimeSelectInput
                        label=""
                        selectedTime={slot}
                        onChange={(value) => updateSlot(key, idx, value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeSlot(key, idx)}
                        className="border-0 bg-transparent p-0 flex-shrink-0"
                        aria-label="Retirer"
                      >
                        <DeleteIcon width="12" fill="#dc3545" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Pricing ────────────────────────────────────────────── */}
      <div style={cardStyle}>
        <SubSectionHeader title="Tarification & participants" />
        <CRow>
          <CCol md={pricingType === "flat" ? 3 : 4}>
            <Field label="Type de tarification" required>
              <CFormSelect {...register(`${namePrefix}.pricingType`)}>
                {PRICING_TYPES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </CFormSelect>
            </Field>
          </CCol>
          {pricingType === "flat" && (
            <>
              <CCol md={3}>
                <Field
                  label="Prix fixe"
                  required
                  error={getIn(errors, `${namePrefix}.flatPrice`)?.message}
                >
                  <CFormInput
                    type="number"
                    step="0.01"
                    {...register(`${namePrefix}.flatPrice`)}
                  />
                </Field>
              </CCol>
              <CCol md={3}>
                <Field
                  label="Prix fixe (€)"
                  error={
                    getIn(errors, `${namePrefix}.flatPriceInEuro`)?.message
                  }
                >
                  <CFormInput
                    type="number"
                    step="0.01"
                    {...register(`${namePrefix}.flatPriceInEuro`)}
                  />
                </Field>
              </CCol>
              <CCol md={3}>
                <Field label="Type de prix" required>
                  <CFormSelect
                    {...register(`${namePrefix}.flatPriceType`)}
                    defaultValue="per_seat"
                  >
                    {FLAT_PRICE_TYPES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </CFormSelect>
                </Field>
              </CCol>
            </>
          )}
          <CCol md={pricingType === "flat" ? 3 : 4}>
            <Field label="Participants min">
              <CFormInput
                type="number"
                min={1}
                {...register(`${namePrefix}.minParticipants`)}
              />
            </Field>
          </CCol>
          <CCol md={pricingType === "flat" ? 3 : 4}>
            <Field label="Participants max" hint="optionnel">
              <CFormInput
                type="number"
                min={1}
                {...register(`${namePrefix}.maxParticipants`)}
              />
            </Field>
          </CCol>
        </CRow>

        {pricingType === "per_age_group" && (
          <div
            className="mt-3 pt-3"
            style={{ borderTop: "1px dashed #e5e5e5" }}
          >
            <SubSectionHeader
              title="Tranches d'âge"
              action={
                <CButton
                  size="sm"
                  style={{
                    backgroundColor: colors.yellow,
                    border: "none",
                    color: "#fff",
                  }}
                  onClick={() =>
                    appendAgeGroup({
                      ageGroup: "adult",
                      endAge: 99,
                      price: 0,
                      priceInEuro: 0,
                      config: { isPermitted: true },
                    })
                  }
                >
                  <PlusIcon width="14" fill="#fff" /> Ajouter une tranche
                </CButton>
              }
            />
            {getIn(errors, `${namePrefix}.ageGroups`)?.message && (
              <div className="text-danger small mb-2">
                {getIn(errors, `${namePrefix}.ageGroups`).message}
              </div>
            )}
            <div className="d-flex flex-column gap-2">
              {ageGroupFields.length === 0 && (
                <div
                  className="text-center py-3 small text-muted"
                  style={{
                    border: "1px dashed #ddd",
                    borderRadius: "10px",
                  }}
                >
                  Aucune tranche d'âge définie
                </div>
              )}
              {ageGroupFields.map((field, index) => {
                const rowErr = getIn(
                  errors,
                  `${namePrefix}.ageGroups.${index}`,
                );
                const isPermitted =
                  watch(
                    `${namePrefix}.ageGroups.${index}.config.isPermitted`,
                  ) ?? true;

                return (
                  <div
                    key={field.id}
                    className="p-2 px-3"
                    style={{
                      border: "1px solid #f0f0f0",
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfc",
                    }}
                  >
                    <CRow className="align-items-end">
                      <CCol md={2}>
                        <Field
                          label="Tranche"
                          error={rowErr?.ageGroup?.message}
                        >
                          <CFormSelect
                            {...register(
                              `${namePrefix}.ageGroups.${index}.ageGroup`,
                            )}
                          >
                            {AGE_GROUPS.map((g) => (
                              <option key={g.value} value={g.value}>
                                {g.label}
                              </option>
                            ))}
                          </CFormSelect>
                        </Field>
                      </CCol>
                      <CCol md={2}>
                        <Field label="Âge max" error={rowErr?.endAge?.message}>
                          <CFormInput
                            type="number"
                            min={0}
                            {...register(
                              `${namePrefix}.ageGroups.${index}.endAge`,
                            )}
                          />
                        </Field>
                      </CCol>
                      <CCol md={2}>
                        <Field label="Prix" error={rowErr?.price?.message}>
                          <CFormInput
                            type="number"
                            step="0.01"
                            min={0}
                            {...register(
                              `${namePrefix}.ageGroups.${index}.price`,
                            )}
                          />
                        </Field>
                      </CCol>
                      <CCol md={2}>
                        <Field
                          label="Prix (€)"
                          error={rowErr?.priceInEuro?.message}
                        >
                          <CFormInput
                            type="number"
                            step="0.01"
                            min={0}
                            {...register(
                              `${namePrefix}.ageGroups.${index}.priceInEuro`,
                            )}
                          />
                        </Field>
                      </CCol>
                      <CCol md={4} className="d-flex flex-wrap gap-3 mb-2">
                        <CFormSwitch
                          label="Non autorisé"
                          checked={!isPermitted}
                          onChange={(e) =>
                            setValue(
                              `${namePrefix}.ageGroups.${index}.config.isPermitted`,
                              !e.target.checked,
                              { shouldValidate: true },
                            )
                          }
                        />
                        <CFormSwitch
                          label="Gratuit"
                          {...register(
                            `${namePrefix}.ageGroups.${index}.config.isFree`,
                          )}
                        />
                        <CFormSwitch
                          label="Adulte requis"
                          {...register(
                            `${namePrefix}.ageGroups.${index}.config.requiresAdult`,
                          )}
                        />
                      </CCol>
                      <CCol md={1} className="mb-2 d-flex justify-content-end">
                        <CButton
                          size="sm"
                          variant="ghost"
                          color="danger"
                          onClick={() => removeAgeGroup(index)}
                        >
                          <DeleteIcon width="14" fill="#dc3545" />
                        </CButton>
                      </CCol>
                    </CRow>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Exceptions ─────────────────────────────────────────── */}
      <div style={cardStyle}>
        <SubSectionHeader
          title="Exceptions (fermetures / horaires spéciaux)"
          action={
            <CButton
              size="sm"
              variant="outline"
              color="secondary"
              onClick={() => appendException({ date: "", slots: null })}
            >
              <PlusIcon width="14" fill={colors.primary} /> Ajouter
            </CButton>
          }
        />

        {exceptionFields.length === 0 && (
          <div
            className="text-center py-3 small text-muted"
            style={{ border: "1px dashed #ddd", borderRadius: "10px" }}
          >
            Aucune exception définie
          </div>
        )}

        <div className="d-flex flex-column gap-2">
          {exceptionFields.map((field, index) => {
            const isClosed =
              watch(`${namePrefix}.exceptions.${index}.slots`) === null;
            return (
              <div
                key={field.id}
                className="p-2 px-3"
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: "10px",
                  backgroundColor: "#fcfcfc",
                }}
              >
                <CRow className="align-items-end">
                  <CCol md={3}>
                    <Field label="Date">
                      <CFormInput
                        type="date"
                        {...register(`${namePrefix}.exceptions.${index}.date`)}
                      />
                    </Field>
                  </CCol>
                  <CCol md={3} className="d-flex align-items-center mb-2">
                    <CFormSwitch
                      label="Fermé toute la journée"
                      checked={isClosed}
                      onChange={(e) =>
                        setValue(
                          `${namePrefix}.exceptions.${index}.slots`,
                          e.target.checked ? null : [],
                          { shouldValidate: true },
                        )
                      }
                    />
                  </CCol>
                  {!isClosed && (
                    <CCol md={4}>
                      <Field label="Horaires (séparés par une virgule)">
                        <CFormInput
                          placeholder="09:00, 14:00"
                          defaultValue={(
                            watch(`${namePrefix}.exceptions.${index}.slots`) ||
                            []
                          ).join(", ")}
                          onBlur={(e) =>
                            setValue(
                              `${namePrefix}.exceptions.${index}.slots`,
                              e.target.value
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean),
                              { shouldValidate: true },
                            )
                          }
                        />
                      </Field>
                    </CCol>
                  )}
                  <CCol
                    md={isClosed ? 6 : 2}
                    className="mb-2 d-flex justify-content-end"
                  >
                    <CButton
                      size="sm"
                      variant="ghost"
                      color="danger"
                      onClick={() => removeException(index)}
                    >
                      <DeleteIcon width="14" fill="#dc3545" /> Retirer
                    </CButton>
                  </CCol>
                </CRow>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
